import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Objectives.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const CATEGORIES = ['Quality', 'Health & Safety', 'Environmental', 'Operational', 'Training', 'Other'];

const STATUS_CONFIG = {
  'on track':    { label: 'On Track',    cls: 'obj-status-ontrack'    },
  'at risk':     { label: 'At Risk',     cls: 'obj-status-atrisk'     },
  'achieved':    { label: 'Achieved',    cls: 'obj-status-achieved'   },
  'missed':      { label: 'Missed',      cls: 'obj-status-missed'     },
  'not started': { label: 'Not Started', cls: 'obj-status-notstarted' },
};

const CATEGORY_COLOURS = {
  'Quality':         '#2196F3',
  'Health & Safety': '#f5b50b',
  'Environmental':   '#4CAF50',
  'Operational':     '#9C27B0',
  'Training':        '#FF9800',
  'Other':           '#607D8B',
};

const EMPTY_FORM = {
  title: '', description: '', category: 'Quality',
  target_value: '', current_value: '', unit: '',
  owner_id: '', start_date: '', target_date: '', review_date: '',
  status: 'not started', management_review: false, notes: '',
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(str) {
  if (!str) return null;
  return Math.ceil((new Date(str) - new Date()) / 86400000);
}

function calcProgress(current, target, status) {
  if (status === 'achieved') return 100;
  const c = parseFloat(current);
  const t = parseFloat(target);
  if (isNaN(c) || isNaN(t) || t === 0) return null;
  return Math.min(100, Math.round((c / t) * 100));
}

function ProgressBar({ current, target, status }) {
  const pct = calcProgress(current, target, status);
  if (pct === null) return null;
  const colour = status === 'achieved' ? '#66bb6a' : status === 'at risk' || status === 'missed' ? '#ef5350' : '#f5b50b';
  return (
    <div className="obj-progress-wrap">
      <div className="obj-progress-bar">
        <div className="obj-progress-fill" style={{ width: `${pct}%`, background: colour }} />
      </div>
      <span className="obj-progress-pct">{pct}%</span>
    </div>
  );
}

export default function Objectives({ user }) {
  const [objectives, setObjectives] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'wizard' | 'form' | 'edit'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  // Wizard state
  const [wizardInput, setWizardInput] = useState('');
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardResult, setWizardResult] = useState(null);
  const [wizardError, setWizardError] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [objRes, userRes] = await Promise.all([
        axios.get(`${API}/objectives`),
        axios.get(`${API}/users/all`),
      ]);
      setObjectives(objRes.data);
      setUsers(userRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const counts = useMemo(() => ({
    total:      objectives.length,
    onTrack:    objectives.filter(o => o.status === 'on track').length,
    atRisk:     objectives.filter(o => o.status === 'at risk').length,
    achieved:   objectives.filter(o => o.status === 'achieved').length,
    notStarted: objectives.filter(o => o.status === 'not started').length,
  }), [objectives]);

  const filtered = useMemo(() => {
    return objectives
      .filter(o => catFilter === 'All' || o.category === catFilter)
      .filter(o => statusFilter === 'All' || o.status === statusFilter);
  }, [objectives, catFilter, statusFilter]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, obj) => {
      const cat = obj.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(obj);
      return acc;
    }, {});
  }, [filtered]);

  const userName = (id) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : '—';
  };

  // ── Wizard ───────────────────────────────────────────────────
  const runWizard = async () => {
    if (!wizardInput.trim()) return;
    setWizardLoading(true);
    setWizardResult(null);
    setWizardError('');
    try {
      const res = await axios.post(`${API}/analyse/objective`, {
        description: wizardInput,
        context: `Bramley Engineering is a crane installation and lifting gear company. They are preparing for an Achilles UVDB B1 audit in June 2026. They have 4 open NCRs from the previous audit. They have recently implemented Vera QMS. Key areas of focus: staff inductions, training records, document control, H&S compliance, environmental compliance.`
      });
      setWizardResult(res.data);
      // Pre-populate form from wizard result
      setForm(prev => ({
        ...prev,
        title: res.data.title || prev.title,
        description: res.data.description || prev.description,
        category: res.data.category || prev.category,
        target_value: res.data.target_value || prev.target_value,
        current_value: res.data.current_value || prev.current_value,
        unit: res.data.unit || prev.unit,
        target_date: res.data.suggested_target_date || prev.target_date,
        management_review: res.data.management_review ?? prev.management_review,
        notes: res.data.notes || prev.notes,
      }));
    } catch (err) {
      setWizardError('Could not generate objective — please try again or fill in manually.');
    } finally {
      setWizardLoading(false);
    }
  };

  const acceptWizardResult = () => {
    setView('form');
  };

  // ── Save ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title) { alert('Title is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        owner_id: form.owner_id || null,
        start_date: form.start_date || null,
        target_date: form.target_date || null,
        review_date: form.review_date || null,
        management_review: form.management_review === true || form.management_review === 'true',
        // If achieved, ensure current_value matches target_value
        current_value: form.status === 'achieved' && form.target_value ? form.target_value : form.current_value,
        created_by: user.id,
      };

      if (selected && view === 'edit') {
        await axios.patch(`${API}/objectives/${selected.id}`, { ...payload, updated_by: user.id });
      } else {
        await axios.post(`${API}/objectives`, payload);
      }

      setView('list');
      setForm(EMPTY_FORM);
      setSelected(null);
      setWizardInput('');
      setWizardResult(null);
      fetchAll();
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleUpdateProgress = async (obj) => {
    const newVal = prompt(`Update current value for "${obj.title}"\nTarget: ${obj.target_value} ${obj.unit || ''}\nCurrent: ${obj.current_value || '0'}`, obj.current_value || '');
    if (newVal === null) return;
    try {
      await axios.patch(`${API}/objectives/${obj.id}`, { current_value: newVal, updated_by: user.id });
      fetchAll();
    } catch (err) { alert('Error updating: ' + err.message); }
  };

  const handleUpdateStatus = async (obj, status) => {
    try {
      const update = { status, updated_by: user.id };
      if (status === 'achieved' && obj.target_value) {
        update.current_value = obj.target_value;
      }
      await axios.patch(`${API}/objectives/${obj.id}`, update);
      fetchAll();
    } catch (err) { alert('Error updating status: ' + err.message); }
  };

  const openEdit = (obj) => {
    setSelected(obj);
    setForm({
      title: obj.title || '', description: obj.description || '',
      category: obj.category || 'Quality', target_value: obj.target_value || '',
      current_value: obj.current_value || '', unit: obj.unit || '',
      owner_id: obj.owner_id || '', start_date: obj.start_date || '',
      target_date: obj.target_date || '', review_date: obj.review_date || '',
      status: obj.status || 'not started',
      management_review: obj.management_review || false, notes: obj.notes || '',
    });
    setView('edit');
  };

  if (loading) return <div className="obj-loading">Loading objectives...</div>;

  // ── WIZARD VIEW ──────────────────────────────────────────────
  if (view === 'wizard') return (
    <div className="obj-form-page">
      <button className="obj-back" onClick={() => { setView('list'); setWizardInput(''); setWizardResult(null); }}>← OBJECTIVES & TARGETS</button>
      <div className="obj-form-header">
        <h2>OBJECTIVE WIZARD</h2>
        <p>Describe what you want to achieve in plain English — Claude will turn it into a properly structured objective</p>
      </div>

      <div className="wizard-input-area">
        <textarea
          className="wizard-textarea"
          rows={4}
          value={wizardInput}
          onChange={e => setWizardInput(e.target.value)}
          placeholder="e.g. Change our H&S provider to BrightHR&#10;e.g. Get all staff inducted before the audit&#10;e.g. Close out the 4 Achilles NCRs&#10;e.g. Produce an environmental legal register"
          onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) runWizard(); }}
        />
        <button className={`wizard-go-btn ${wizardLoading ? 'loading' : ''}`} onClick={runWizard} disabled={wizardLoading || !wizardInput.trim()}>
          {wizardLoading ? <><span className="wizard-spinner" />Generating...</> : '✦ Generate Objective'}
        </button>
        {wizardError && <div className="wizard-error">{wizardError}</div>}
      </div>

      {wizardResult && (
        <div className="wizard-result">
          <div className="wizard-result-header">
            <span className="wizard-result-tag">Claude suggests:</span>
            <span className="wizard-rationale">{wizardResult.rationale}</span>
          </div>

          <div className="wizard-result-card">
            <div className="wizard-result-title">{wizardResult.title}</div>
            {wizardResult.description && <div className="wizard-result-desc">{wizardResult.description}</div>}

            <div className="wizard-result-chips">
              <span className="wizard-chip cat" style={{ borderColor: CATEGORY_COLOURS[wizardResult.category] || '#555', color: CATEGORY_COLOURS[wizardResult.category] || '#888' }}>
                {wizardResult.category}
              </span>
              {wizardResult.target_value && (
                <span className="wizard-chip">Target: {wizardResult.target_value} {wizardResult.unit}</span>
              )}
              {wizardResult.suggested_target_date && (
                <span className="wizard-chip">Due: {formatDate(wizardResult.suggested_target_date)}</span>
              )}
              {wizardResult.management_review && (
                <span className="wizard-chip mr">Management Review Item</span>
              )}
            </div>

            {wizardResult.notes && (
              <div className="wizard-result-notes">
                <span className="wizard-notes-label">How to measure:</span> {wizardResult.notes}
              </div>
            )}
          </div>

          <div className="wizard-result-actions">
            <button className="obj-btn secondary" onClick={() => { setWizardResult(null); setWizardInput(''); }}>TRY AGAIN</button>
            <button className="obj-btn secondary" onClick={() => setView('form')}>EDIT BEFORE SAVING</button>
            <button className="obj-btn primary" onClick={handleSave} disabled={saving}>
              {saving ? 'SAVING...' : '✓ SAVE THIS OBJECTIVE'}
            </button>
          </div>
        </div>
      )}

      {!wizardResult && !wizardLoading && (
        <div className="wizard-suggestions">
          <div className="wizard-suggestions-title">SUGGESTED FOR BRAMLEY RIGHT NOW</div>
          {[
            'Complete staff inductions for all 7 employees before the June audit',
            'Close out all 4 Achilles NCRs from the 2025 audit',
            'Produce an environmental legal register',
            'Produce an H&S legislation compliance register',
            'Change H&S provider from THSP to BrightHR',
            'Implement Vera QMS across the business',
            'Achieve zero accidents and incidents this year',
            'Complete pre-use inspection records for all company vehicles',
          ].map((s, i) => (
            <button key={i} className="wizard-suggestion-chip" onClick={() => setWizardInput(s)}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── FORM VIEW (manual or post-wizard) ───────────────────────
  if (view === 'form' || view === 'edit') return (
    <div className="obj-form-page">
      <button className="obj-back" onClick={() => { setView(view === 'edit' ? 'list' : 'wizard'); setSelected(null); }}>
        {view === 'edit' ? '← OBJECTIVES & TARGETS' : '← WIZARD'}
      </button>
      <div className="obj-form-header">
        <h2>{view === 'edit' ? 'EDIT OBJECTIVE' : 'CONFIRM OBJECTIVE'}</h2>
        {view === 'edit' && selected && <p className="obj-ref">{selected.reference_number}</p>}
        {view === 'form' && <p>Review and adjust before saving</p>}
      </div>

      <div className="obj-form">
        <div className="obj-field full">
          <label>TITLE *</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="obj-field full">
          <label>DESCRIPTION</label>
          <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="obj-field">
          <label>CATEGORY</label>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="obj-field">
          <label>OWNER</label>
          <select value={form.owner_id} onChange={e => setForm({ ...form, owner_id: e.target.value })}>
            <option value="">— Select owner —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
        <div className="obj-field">
          <label>STATUS</label>
          <select value={form.status} onChange={e => {
            const newStatus = e.target.value;
            const updates = { status: newStatus };
            if (newStatus === 'achieved' && form.target_value) {
              updates.current_value = form.target_value;
            }
            setForm({ ...form, ...updates });
          }}>
            <option value="not started">Not Started</option>
            <option value="on track">On Track</option>
            <option value="at risk">At Risk</option>
            <option value="achieved">Achieved</option>
            <option value="missed">Missed</option>
          </select>
        </div>
        <div className="obj-field">
          <label>MANAGEMENT REVIEW ITEM?</label>
          <select value={form.management_review} onChange={e => setForm({ ...form, management_review: e.target.value === 'true' })}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div className="obj-field-group-title">MEASUREMENT</div>
        <div className="obj-field">
          <label>TARGET VALUE</label>
          <input value={form.target_value} onChange={e => setForm({ ...form, target_value: e.target.value })} placeholder="e.g. 100" />
        </div>
        <div className="obj-field">
          <label>CURRENT VALUE</label>
          <input value={form.current_value} onChange={e => setForm({ ...form, current_value: e.target.value })} placeholder="e.g. 2" />
        </div>
        <div className="obj-field">
          <label>UNIT</label>
          <input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="e.g. % staff inducted" />
        </div>
        <div className="obj-field-group-title">DATES</div>
        <div className="obj-field">
          <label>START DATE</label>
          <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
        </div>
        <div className="obj-field">
          <label>TARGET DATE{form.target_date && new Date(form.target_date) < new Date() && form.status !== 'achieved' && form.status !== 'missed' ? <span className="obj-date-warning"> — ⚠ past due</span> : ''}</label>
          <input type="date" value={form.target_date} onChange={e => setForm({ ...form, target_date: e.target.value })}
            style={form.target_date && new Date(form.target_date) < new Date() && form.status !== 'achieved' && form.status !== 'missed' ? { borderColor: '#f5b50b' } : {}} />
        </div>
        <div className="obj-field">
          <label>REVIEW DATE</label>
          <input type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} />
        </div>
        <div className="obj-field full">
          <label>NOTES / EVIDENCE</label>
          <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="obj-form-actions">
          <button className="obj-btn secondary" onClick={() => { setView(view === 'edit' ? 'list' : 'wizard'); setSelected(null); }}>CANCEL</button>
          <button className={`obj-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? 'SAVING...' : view === 'edit' ? 'SAVE CHANGES' : 'SAVE OBJECTIVE'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── LIST VIEW ────────────────────────────────────────────────
  return (
    <div className="obj-page">
      <div className="obj-stats">
        <div className="obj-stat" onClick={() => setStatusFilter('All')} style={{ cursor: 'pointer' }}>
          <span className="obj-stat-value">{counts.total}</span>
          <span className="obj-stat-label">Total</span>
        </div>
        <div className="obj-stat ontrack" onClick={() => setStatusFilter('on track')} style={{ cursor: 'pointer' }}>
          <span className="obj-stat-value">{counts.onTrack}</span>
          <span className="obj-stat-label">On Track</span>
        </div>
        <div className="obj-stat atrisk" onClick={() => setStatusFilter('at risk')} style={{ cursor: 'pointer' }}>
          <span className="obj-stat-value">{counts.atRisk}</span>
          <span className="obj-stat-label">At Risk</span>
        </div>
        <div className="obj-stat achieved" onClick={() => setStatusFilter('achieved')} style={{ cursor: 'pointer' }}>
          <span className="obj-stat-value">{counts.achieved}</span>
          <span className="obj-stat-label">Achieved</span>
        </div>
        <div className="obj-stat notstarted" onClick={() => setStatusFilter('not started')} style={{ cursor: 'pointer' }}>
          <span className="obj-stat-value">{counts.notStarted}</span>
          <span className="obj-stat-label">Not Started</span>
        </div>
      </div>

      <div className="obj-controls">
        <div className="obj-filter-group">
          {['All', ...CATEGORIES].map(c => (
            <button key={c} className={`obj-filter-btn ${catFilter === c ? 'active' : ''}`} onClick={() => setCatFilter(c)}>
              {c.toUpperCase()}
            </button>
          ))}
        </div>
        {user.role === 'admin' && (
          <div className="obj-add-group">
            <button className="obj-wizard-btn" onClick={() => { setForm(EMPTY_FORM); setWizardInput(''); setWizardResult(null); setView('wizard'); }}>
              ✦ OBJECTIVE WIZARD
            </button>
            <button className="obj-add-btn" onClick={() => { setForm(EMPTY_FORM); setView('form'); }}>+ MANUAL</button>
          </div>
        )}
      </div>

      {objectives.length === 0 ? (
        <div className="obj-empty">
          <div className="obj-empty-icon">🎯</div>
          <p>No objectives set yet.</p>
          {user.role === 'admin' && (
            <button className="obj-btn primary" onClick={() => { setForm(EMPTY_FORM); setView('wizard'); }}>
              ✦ Use the Objective Wizard
            </button>
          )}
        </div>
      ) : (
        Object.keys(grouped).sort().map(cat => (
          <div key={cat} className="obj-category-group">
            <div className="obj-category-header">
              <span className="obj-category-dot" style={{ background: CATEGORY_COLOURS[cat] || '#607D8B' }} />
              <h3>{cat}</h3>
              <span className="obj-category-count">{grouped[cat].length}</span>
            </div>
            <div className="obj-list">
              {grouped[cat].map(obj => {
                const pct = calcProgress(obj.current_value, obj.target_value);
                const due = daysUntil(obj.target_date);
                const status = STATUS_CONFIG[obj.status] || STATUS_CONFIG['not started'];
                return (
                  <div key={obj.id} className="obj-card">
                    <div className="obj-card-top">
                      <div>
                        <div className="obj-card-ref">{obj.reference_number}</div>
                        <div className="obj-card-title">{obj.title}</div>
                        {obj.description && <div className="obj-card-desc">{obj.description}</div>}
                      </div>
                      <div className="obj-card-badges">
                        <span className={`obj-status-badge ${status.cls}`}>{status.label}</span>
                        {obj.management_review && <span className="obj-mgmt-badge" title="Management Review Item">MR</span>}
                      </div>
                    </div>
                    {(obj.target_value || obj.current_value || obj.status === 'achieved') && (
                      <div className="obj-card-progress">
                        <div className="obj-progress-labels">
                          {obj.status === 'achieved' && !obj.target_value
                            ? <span>Achieved</span>
                            : <span>Progress: {obj.current_value || '0'} / {obj.target_value} {obj.unit || ''}</span>
                          }
                          {pct !== null && <span>{pct}%</span>}
                        </div>
                        <ProgressBar current={obj.current_value} target={obj.target_value} status={obj.status} />
                      </div>
                    )}
                    <div className="obj-card-footer">
                      <div className="obj-card-meta">
                        {obj.owner && <span>👤 {obj.owner.first_name} {obj.owner.last_name}</span>}
                        {obj.target_date && (
                          <span className={due !== null && due < 0 ? 'obj-overdue' : due !== null && due < 30 ? 'obj-urgent' : ''}>
                            🎯 {formatDate(obj.target_date)}
                            {due !== null && obj.status !== 'achieved' && (
                              <span className="obj-due-chip">{due < 0 ? `${Math.abs(due)}d overdue` : `${due}d left`}</span>
                            )}
                          </span>
                        )}
                      </div>
                      {user.role === 'admin' && (
                        <div className="obj-card-actions">
                          {obj.target_value && obj.status !== 'achieved' && (
                            <button className="obj-action-btn" onClick={() => handleUpdateProgress(obj)}>UPDATE</button>
                          )}
                          {obj.status !== 'achieved' && (
                            <button className="obj-action-btn green" onClick={() => handleUpdateStatus(obj, 'achieved')}>✓ ACHIEVED</button>
                          )}
                          <button className="obj-action-btn" onClick={() => openEdit(obj)}>EDIT</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
